import { Modal as AntdModal, type ModalProps as AntdModalProps } from "antd"
import { useElement } from "hooks/useElement"
import { cn } from "styles/cn"

export type ModalProps = AntdModalProps & {
	/**
	 * When true, the modal goes fullscreen on mobile (< @2xl)
	 * and returns to normal modal dimensions at @2xl and above.
	 * @default false
	 */
	fullscreen?: boolean
}

export const Modal = ({
	className,
	classNames,
	fullscreen = false,
	cancelButtonProps,
	okButtonProps,
	...props
}: ModalProps) => {
	const containerElement = useElement("layout-content")
	const customClassNames =
		typeof classNames === "object" && classNames !== null
			? classNames
			: undefined

	return (
		<AntdModal
			{...props}
			centered
			destroyOnHidden
			getContainer={containerElement!}
			className={cn(
				"flex max-h-full max-w-full flex-col",
				fullscreen && "@max-2xl:size-full!",
				className,
			)}
			classNames={{
				...customClassNames,
				mask: cn("absolute", customClassNames?.mask),
				wrapper: cn(
					"absolute flex items-center justify-center overflow-hidden",
					fullscreen ? "p-0 @2xl:p-15" : "p-4 @2xl:p-15",
					customClassNames?.wrapper,
				),
				container: cn(
					"flex min-h-0 flex-col overflow-hidden",
					fullscreen
						? "size-full rounded-none p-4 @2xl:size-auto @2xl:max-h-full @2xl:rounded-2xl @2xl:p-8"
						: "max-h-full rounded-2xl p-6 @2xl:p-8",
					customClassNames?.container,
				),
				header: cn(
					"font-headings *:text-xl *:font-bold",
					customClassNames?.header,
				),
				body: cn("overflow-y-auto py-2", customClassNames?.body),
				footer: cn("flex items-center justify-end", customClassNames?.footer),
			}}
			cancelButtonProps={{
				size: "large",
				...cancelButtonProps,
			}}
			okButtonProps={{
				size: "large",
				...okButtonProps,
			}}
		/>
	)
}
